"use client";

import { Icon } from "@afa-shared/afa-components";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Select from "@afa-shared/afa-components/dist/Inputs/Select";
import { useQueryState } from "nuqs";
import { useCallback, useEffect, useId, useState } from "react";

import { FaqFilterOption } from "types/faqFilters";

import { filterClickTracker } from "./filterTracker";
import filterStyles from "./filters.module.css";

interface FilterDropdownsProps {
  insuranceOptions?: FaqFilterOption[];
  subjectOptions?: FaqFilterOption[];
}

export const FilterDropdowns = ({
  insuranceOptions = DEFAULT_INSURANCE_OPTIONS,
  subjectOptions = DEFAULT_SUBJECT_OPTIONS,
}: FilterDropdownsProps) => {
  const [insurance, setInsurance] = useQueryState("insurance", { defaultValue: "", shallow: true });
  const [subject, setSubject] = useQueryState("subject", { defaultValue: "", shallow: true });
  const [isHydrated, setIsHydrated] = useState(false);
  const insuranceId = useId();
  const subjectId = useId();

  const hasActiveFilter = Boolean(insurance || subject);
  const shouldShowClearButton = isHydrated && hasActiveFilter;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleInsuranceChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setInsurance(value || null);
      filterClickTracker(value || undefined, subject || undefined);
    },
    [subject, setInsurance],
  );

  const handleSubjectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      setSubject(value || null);
      filterClickTracker(insurance || undefined, value || undefined);
    },
    [insurance, setSubject],
  );

  const handleClearFilters = useCallback(() => {
    setInsurance(null);
    setSubject(null);
  }, [setInsurance, setSubject]);

  return (
    <div className={filterStyles.filterContainer}>
      <Heading variant="h2" removeMargin>
        Filtrera frågor och svar
      </Heading>
      <div className={filterStyles.dropdownRow}>
        <div>
          <Select
            id={insuranceId}
            labelText="Händelse"
            placeholder="Alla händelser"
            options={insuranceOptions}
            selected={insurance}
            onChange={handleInsuranceChange}
            width="100%"
            name="filter-insurance"
          />
        </div>
        <div>
          <Select
            id={subjectId}
            labelText="Ämne"
            placeholder="Alla ämnen"
            options={subjectOptions}
            selected={subject}
            onChange={handleSubjectChange}
            width="100%"
            name="filter-subject"
          />
        </div>
      </div>
      {shouldShowClearButton && (
        <button type="button" className={filterStyles.clearButton} onClick={handleClearFilters}>
          <Icon name="delete" />
          Rensa filter
        </button>
      )}
    </div>
  );
};

const DEFAULT_INSURANCE_OPTIONS: FaqFilterOption[] = [{ value: "", name: "Alla händelser" }];

const DEFAULT_SUBJECT_OPTIONS: FaqFilterOption[] = [{ value: "", name: "Alla ämnen" }];

export default FilterDropdowns;
